import type { GroupStyleI, LineI, LineStrokeAlignT, SimpleStyleT, StyleUnitT } from "tweenly"
import { lineAxisFraction } from "tweenly"
import {
    findTransform,
    getRotation,
    roundTo2,
    setRotation,
    setTransform,
} from "./transform.helpers"

/**
 * Line object styles — weight, dash pattern and caps (see data/styles/simple/stroke.styles).
 * Pure: they take the object's styles and return the updated array for the caller to write.
 */

export type LineStylePresetT = "solid" | "dashed" | "dotted"
export const LINE_STYLE_PRESETS: LineStylePresetT[] = ["solid", "dashed", "dotted"]
export const LINE_WEIGHT_UNITS: StyleUnitT[] = ["px", "pt"]

/** 1pt = 4/3px (CSS reference pixel). */
const PX_PER_PT = 4 / 3

const findStyle = (styles: SimpleStyleT[], property: string) =>
    styles.find((style) => style.property === property)

const withStyle = (styles: SimpleStyleT[], property: string, changes: Partial<SimpleStyleT>) =>
    styles.map((style) => (style.property === property ? { ...style, ...changes } : style))

/** The dash pattern as `[dash, gap]`, never NaN. */
export const getDashPattern = (styles: SimpleStyleT[]): [number, number] => {
    const value = findStyle(styles, "strokeDasharray")?.value
    return [Number(value?.[0]) || 0, Number(value?.[1]) || 0]
}

/** Which preset a dash pattern looks like: no gap is solid, no dash (just caps) is dotted. */
export const getLineStylePreset = ([dash, gap]: [number, number]): LineStylePresetT => {
    if (gap <= 0) return "solid"
    return dash <= 0 ? "dotted" : "dashed"
}

/** The line's weight in px, whatever unit it is set in. */
export const getLineWeightPx = (styles: SimpleStyleT[]): number => {
    const style = findStyle(styles, "lineWeight")
    const value = Number(style?.value) || 0
    return style?.unit === "pt" ? value * PX_PER_PT : value
}

/**
 * Switch the weight to another unit, converting the value so the line keeps its thickness.
 */
export const setLineWeightUnit = (styles: SimpleStyleT[], unit: StyleUnitT): SimpleStyleT[] => {
    const style = findStyle(styles, "lineWeight")
    if (!style || style.unit === unit) return styles
    const px = getLineWeightPx(styles)
    const value = unit === "pt" ? px / PX_PER_PT : px
    return withStyle(styles, "lineWeight", { value: roundTo2(value), unit })
}

/**
 * Apply a preset: a dash pattern proportional to the weight, and for dots the round caps
 * that make a zero-length dash visible at all.
 */
export const applyLineStylePreset = (
    styles: SimpleStyleT[],
    preset: LineStylePresetT
): SimpleStyleT[] => {
    const weight = Math.max(1, getLineWeightPx(styles))
    switch (preset) {
        case "solid":
            return withStyle(styles, "strokeDasharray", { value: [0, 0] })
        case "dashed":
            return withStyle(styles, "strokeDasharray", {
                value: [Math.round(weight * 3), Math.round(weight * 2)],
            })
        case "dotted":
            return withStyle(
                withStyle(styles, "strokeDasharray", { value: [0, Math.round(weight * 2)] }),
                "strokeLinecap",
                { value: "round" }
            )
    }
}

// ============================================================================
// Geometry — the line between its two end points
// ============================================================================

export type PointT = { x: number; y: number }

/** Where a line's box is and how it is transformed — the parts its end points depend on. */
export type LineFrameT = {
    left: number
    top: number
    width: number
    /** the weight, in px */
    height: number
    rotation: number
    scaleX: number
    scaleY: number
    /** translate as written: px, or % of the box (percentOf* flag the latter) */
    translateX: number
    translateY: number
    translateXIsPercent: boolean
    translateYIsPercent: boolean
    /** transform-origin as fractions of the box */
    originX: number
    originY: number
    /** where the axis between the end points runs through the box, as a fraction of its height */
    axisY: number
}

const ORIGINS: Record<string, [number, number]> = {
    center: [0.5, 0.5],
    "top left": [0, 0],
    "top right": [1, 0],
    "bottom left": [0, 1],
    "bottom right": [1, 1],
}

const numberOr = (value: unknown, fallback: number): number => {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

/**
 * The line's frame from its styles, transforms and stroke position.
 *
 * Rotation, scale and translate are honoured; rotateX/Y and matrix are not — a line is a
 * flat thing in the editor's eyes, and they stay at their identity defaults.
 */
export const getLineFrame = ({
    styles,
    transforms,
    strokeAlign,
}: Pick<LineI, "styles" | "transforms" | "strokeAlign">): LineFrameT => {
    const translateX = findTransform(transforms, "translateX")
    const translateY = findTransform(transforms, "translateY")
    const [originX, originY] = ORIGINS[String(findStyle(styles, "transformOrigin")?.value)] ?? [
        0.5, 0.5,
    ]
    return {
        left: numberOr(findStyle(styles, "left")?.value, 0),
        top: numberOr(findStyle(styles, "top")?.value, 0),
        width: numberOr(findStyle(styles, "width")?.value, 0),
        height: getLineWeightPx(styles),
        rotation: getRotation(transforms),
        scaleX: numberOr(findTransform(transforms, "scaleX")?.value, 1),
        scaleY: numberOr(findTransform(transforms, "scaleY")?.value, 1),
        translateX: numberOr(translateX?.value, 0),
        translateY: numberOr(translateY?.value, 0),
        translateXIsPercent: translateX?.unit === "%",
        translateYIsPercent: translateY?.unit === "%",
        originX,
        originY,
        axisY: lineAxisFraction(strokeAlign),
    }
}

/**
 * Where a point of the box (in the box's own, untransformed coordinates) is drawn, in the
 * parent's coordinates. CSS applies the transform list right to left — rotate, then
 * translate, then scale — around the transform origin.
 */
const transformPoint = (frame: LineFrameT, width: number, local: PointT): PointT => {
    const angle = (frame.rotation * Math.PI) / 180
    const originX = frame.originX * width
    const originY = frame.originY * frame.height
    const dx = local.x - originX
    const dy = local.y - originY
    const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle)
    const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle)
    const translateX = frame.translateXIsPercent
        ? (frame.translateX / 100) * width
        : frame.translateX
    const translateY = frame.translateYIsPercent
        ? (frame.translateY / 100) * frame.height
        : frame.translateY
    return {
        x: frame.left + originX + frame.scaleX * (rotatedX + translateX),
        y: frame.top + originY + frame.scaleY * (rotatedY + translateY),
    }
}

/** The line's start and end point as drawn, in the parent's coordinates. */
export const getLineEndpoints = (frame: LineFrameT): [PointT, PointT] => [
    transformPoint(frame, frame.width, { x: 0, y: frame.axisY * frame.height }),
    transformPoint(frame, frame.width, { x: frame.width, y: frame.axisY * frame.height }),
]

/**
 * The left / top / width / rotation that draw the line from `start` to `end` (parent
 * coordinates), keeping its weight, scale, translate and transform origin. Null when a zero
 * scale makes that impossible.
 */
export const lineFrameFromEndpoints = (
    frame: LineFrameT,
    start: PointT,
    end: PointT
): Pick<LineFrameT, "left" | "top" | "width" | "rotation"> | null => {
    if (frame.scaleX === 0 || frame.scaleY === 0) return null
    // undo the scale to get the direction the box itself has to point in
    const unscaledX = (end.x - start.x) / frame.scaleX
    const unscaledY = (end.y - start.y) / frame.scaleY
    const width = roundTo2(Math.hypot(unscaledX, unscaledY))
    const rotation = roundTo2((Math.atan2(unscaledY, unscaledX) * 180) / Math.PI)

    // place the box so that its start lands exactly on `start`
    const placed = transformPoint({ ...frame, left: 0, top: 0, rotation }, width, {
        x: 0,
        y: frame.axisY * frame.height,
    })
    return {
        left: roundTo2(start.x - placed.x),
        top: roundTo2(start.y - placed.y),
        width,
        rotation,
    }
}

/** `point` moved onto the nearest 45° direction from `anchor`, at the same distance. */
export const snapTo45 = (anchor: PointT, point: PointT): PointT => {
    const distance = Math.hypot(point.x - anchor.x, point.y - anchor.y)
    const step = Math.PI / 4
    const angle = Math.round(Math.atan2(point.y - anchor.y, point.x - anchor.x) / step) * step
    return { x: anchor.x + distance * Math.cos(angle), y: anchor.y + distance * Math.sin(angle) }
}

/** The line's styles and transforms set to `next` — what a drag of an end point commits. */
export const applyLineFrame = (
    styles: SimpleStyleT[],
    transforms: GroupStyleI[],
    next: Pick<LineFrameT, "left" | "top" | "width" | "rotation">
): { styles: SimpleStyleT[]; transforms: GroupStyleI[] } => ({
    styles: styles.map((style) => {
        if (style.property === "left") return { ...style, value: next.left }
        if (style.property === "top") return { ...style, value: next.top }
        if (style.property === "width") return { ...style, value: next.width }
        return style
    }),
    // not normalised: the angle has to stay the one the geometry was computed for
    transforms: setTransform(transforms, "rotateZ", next.rotation, "deg"),
})

/**
 * Move the stroke to another side of the line's axis. The end points stay where they are —
 * as in Figma, only the stroke moves — so the box is shifted to keep them in place. Returns
 * the styles to write together with the new `strokeAlign`.
 */
export const setLineStrokeAlign = (
    line: Pick<LineI, "styles" | "transforms" | "strokeAlign">,
    strokeAlign: LineStrokeAlignT
): SimpleStyleT[] => {
    const frame = getLineFrame(line)
    const [start, end] = getLineEndpoints(frame)
    const next = lineFrameFromEndpoints(
        { ...frame, axisY: lineAxisFraction(strokeAlign) },
        start,
        end
    )
    if (!next) return line.styles
    // the rotation stays exactly what it was — only the box moves
    return applyLineFrame(line.styles, line.transforms, { ...next, rotation: frame.rotation })
        .styles
}

// ============================================================================
// Flips — a line mirrors by turning, so its scale stays 1 and its end points stay where
// they are mirrored to
// ============================================================================

/** Mirror the line across the vertical (x) or horizontal (y) axis through its centre. */
export const flipLineRotation = (transforms: GroupStyleI[], axis: "x" | "y"): GroupStyleI[] => {
    const rotation = getRotation(transforms)
    return setRotation(transforms, axis === "x" ? 180 - rotation : -rotation)
}
